'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { routingListMachine } from './machines/routingListMachine'
import { evaluateRoutingRules, autoAssignToAgent } from './routing-engine'
import type {
  RoutingRule,
  ProcessingQueue,
  AgentSkill,
  RoutingRequest,
  RoutingResult,
} from './types'

export type RoutingListMachineApi = ReturnType<typeof useMachine<typeof routingListMachine>>

const RoutingListMachineContext = createContext<RoutingListMachineApi | null>(null)

export function RoutingListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(routingListMachine)
  return (
    <RoutingListMachineContext.Provider value={api}>{children}</RoutingListMachineContext.Provider>
  )
}

export function useRoutingListMachine() {
  const ctx = useContext(RoutingListMachineContext)
  if (!ctx) {
    throw new Error('useRoutingListMachine must be used within RoutingListMachineProvider')
  }
  return ctx
}

export function useRoutingListMachineOptional() {
  return useContext(RoutingListMachineContext)
}

export function useRoutingListStoreCompat() {
  const machineApi = useRoutingListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const ctx = state.context
    const getRuleById = (id: string) => ctx.rules.find((r) => r.id === id)
    const getActiveRules = () => ctx.rules.filter((r) => r.isActive)
    const getQueueById = (id: string) => ctx.queues.find((q) => q.id === id)
    return {
      rules: ctx.rules,
      queues: ctx.queues,
      agentSkills: ctx.agentSkills,
      setRules: (rules: RoutingRule[]) => send({ type: 'SET_RULES', rules }),
      addRule: (rule: RoutingRule) => send({ type: 'ADD_RULE', rule }),
      updateRule: (id: string, updates: Partial<RoutingRule>) =>
        send({ type: 'UPDATE_RULE', id, updates }),
      deleteRule: (id: string) => send({ type: 'DELETE_RULE', id }),
      getRuleById,
      getActiveRules,
      setQueues: (queues: ProcessingQueue[]) => send({ type: 'SET_QUEUES', queues }),
      addQueue: (queue: ProcessingQueue) => send({ type: 'ADD_QUEUE', queue }),
      updateQueue: (id: string, updates: Partial<ProcessingQueue>) =>
        send({ type: 'UPDATE_QUEUE', id, updates }),
      deleteQueue: (id: string) => send({ type: 'DELETE_QUEUE', id }),
      getQueueById,
      incrementQueueLoad: (id: string) => send({ type: 'INCREMENT_QUEUE_LOAD', id }),
      decrementQueueLoad: (id: string) => send({ type: 'DECREMENT_QUEUE_LOAD', id }),
      setAgentSkills: (skills: AgentSkill[]) => send({ type: 'SET_AGENT_SKILLS', skills }),
      updateAgentSkills: (agentId: string, skills: Partial<AgentSkill>) =>
        send({ type: 'UPDATE_AGENT_SKILLS', agentId, skills }),
      route: (request: RoutingRequest): RoutingResult | null => {
        const rules = getActiveRules()
        return evaluateRoutingRules(request, rules)
      },
      assignAgent: (queueId: string, requiredSkills?: string[]) => {
        const queue = getQueueById(queueId)
        if (!queue) return null
        return autoAssignToAgent(queue, ctx.agentSkills, requiredSkills)
      },
    }
  }, [machineApi])
}

export function useRoutingActions() {
  const machineApi = useRoutingListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setRules: (rules: RoutingRule[]) => send({ type: 'SET_RULES', rules }),
    addRule: (rule: RoutingRule) => send({ type: 'ADD_RULE', rule }),
    updateRule: (id: string, updates: Partial<RoutingRule>) =>
      send({ type: 'UPDATE_RULE', id, updates }),
    deleteRule: (id: string) => send({ type: 'DELETE_RULE', id }),
    setQueues: (queues: ProcessingQueue[]) => send({ type: 'SET_QUEUES', queues }),
    addQueue: (queue: ProcessingQueue) => send({ type: 'ADD_QUEUE', queue }),
    updateQueue: (id: string, updates: Partial<ProcessingQueue>) =>
      send({ type: 'UPDATE_QUEUE', id, updates }),
    deleteQueue: (id: string) => send({ type: 'DELETE_QUEUE', id }),
    incrementQueueLoad: (id: string) => send({ type: 'INCREMENT_QUEUE_LOAD', id }),
    decrementQueueLoad: (id: string) => send({ type: 'DECREMENT_QUEUE_LOAD', id }),
    setAgentSkills: (skills: AgentSkill[]) => send({ type: 'SET_AGENT_SKILLS', skills }),
    updateAgentSkills: (agentId: string, skills: Partial<AgentSkill>) =>
      send({ type: 'UPDATE_AGENT_SKILLS', agentId, skills }),
  }), [send])
}

export function useRouteTicket(request: RoutingRequest): RoutingResult | null {
  const compat = useRoutingListStoreCompat()
  return useMemo(() => compat.route(request), [compat, request])
}

export function useQueueStats(queueId: string) {
  const compat = useRoutingListStoreCompat()
  return useMemo(() => {
    const queue = compat.getQueueById(queueId)
    if (!queue) {
      return {
        exists: false,
        currentLoad: 0,
        maxCapacity: 0,
        loadPercentage: 0,
        hasCapacity: false,
        agentCount: 0,
      }
    }
    const loadPercentage = queue.maxCapacity ? (queue.currentLoad / queue.maxCapacity) * 100 : 0
    return {
      exists: true,
      currentLoad: queue.currentLoad,
      maxCapacity: queue.maxCapacity || 0,
      loadPercentage,
      hasCapacity: !queue.maxCapacity || queue.currentLoad < queue.maxCapacity,
      agentCount: queue.agentIds.length,
    }
  }, [compat, queueId])
}
